import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  proTeams,
  type ManagedTeam,
  type TeamAction,
  type TeamCommand,
} from "../../api/proTeams";
import { Button, Dialog, TextInput, Select } from "../../ui";
import { failureMessage, parseTeamId, validSteamId } from "./state";

export type TeamForm = {
  action: TeamAction;
  team?: ManagedTeam;
  target?: string;
};
export const actionTitles: Record<TeamAction, string> = {
  create: "Create team",
  description: "Edit description",
  logo: "Upload team interface logo",
  banner: "Upload banner logo",
  sponsor: "Upload sponsor logo",
  base: "Upload base logo",
  import: "Connect existing team",
  edit: "Edit team details",
  invite: "Invite player",
  kick: "Remove member",
  transfer: "Transfer administration",
  leave: "Leave team",
  archive: "Archive team",
  sync: "Sync roster",
};

export function TeamActionDialog({
  form,
  scope,
  locked,
  error,
  onClose,
  onSubmit,
}: {
  form: TeamForm;
  scope: string;
  locked: boolean;
  error: unknown;
  onClose: () => void;
  onSubmit: (
    action: TeamAction,
    teamId: number | undefined,
    body: TeamCommand,
  ) => void;
}) {
  const [botId, setBotId] = useState("");
  const [teamId, setTeamId] = useState("");
  const [target, setTarget] = useState(form.target ?? "");
  const [name, setName] = useState(form.team?.snapshot.name ?? "");
  const [tag, setTag] = useState(form.team?.snapshot.tag ?? "");
  const [country, setCountry] = useState(
    form.team?.snapshot.country_code ?? "",
  );
  const [website, setWebsite] = useState(form.team?.snapshot.url ?? "");
  const [description, setDescription] = useState(form.team?.description ?? "");
  const [image, setImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [readingImage, setReadingImage] = useState(false);
  async function selectImage(file?: File) {
    setImage("");
    setImageError("");
    if (!file) return;
    if (
      !["image/png", "image/jpeg"].includes(file.type) ||
      file.size > 512 * 1024 ||
      file.size === 0
    ) {
      setImageError("Choose a PNG or JPEG image no larger than 512 KiB.");
      return;
    }
    setReadingImage(true);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error("Could not read this image."));
        reader.readAsDataURL(file);
      });
      setImage(data);
    } catch {
      setImageError("Could not read this image.");
    } finally {
      setReadingImage(false);
    }
  }
  const choosingBot = form.action === "create" || form.action === "import";
  const bots = useQuery({
    queryKey: ["pro-teams", scope, "bots"],
    queryFn: () => proTeams.bots(),
    enabled: choosingBot,
    retry: false,
  });
  const selectedBot = bots.data?.find((bot) => bot.bot_id === botId);
  const capacity = useQuery({
    queryKey: ["pro-teams", scope, "capacity", botId],
    queryFn: () => proTeams.capacity(botId),
    enabled: choosingBot && !!selectedBot,
    retry: false,
  });
  const botReady =
    !!selectedBot &&
    !bots.isError &&
    !!capacity.data &&
    !capacity.isError &&
    !capacity.isFetching;
  const destructive = ["kick", "transfer", "leave", "archive"].includes(
    form.action,
  );
  const valid = ["logo", "banner", "sponsor", "base"].includes(form.action)
    ? !!image && !readingImage && !imageError
    : form.action === "create"
      ? !!name.trim() &&
        !!tag.trim() &&
        botReady &&
        !!capacity.data?.can_create &&
        !capacity.isError &&
        !capacity.isFetching
      : form.action === "import"
        ? parseTeamId(teamId) !== null &&
          botReady &&
          !!capacity.data &&
          !capacity.isError &&
          (capacity.data.policy_max_managed_teams == null ||
            capacity.data.managed_teams <
              capacity.data.policy_max_managed_teams)
        : form.action === "invite" ||
            form.action === "kick" ||
            form.action === "transfer"
          ? validSteamId(target.trim())
          : form.action === "edit"
            ? !!name.trim() && !!tag.trim()
            : true;
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!valid || locked) return;
    let body: TeamCommand = {};
    if (["logo", "banner", "sponsor", "base"].includes(form.action))
      body.image_data = image.split(",")[1];
    if (form.action === "create") body.bot_id = botId.trim();
    if (
      form.action === "description" ||
      (form.action === "create" && description.trim())
    )
      body.description = description.trim();
    if (form.action === "import")
      body = { bot_id: botId.trim(), team_id: parseTeamId(teamId)! };
    if (
      form.action === "invite" ||
      form.action === "kick" ||
      form.action === "transfer"
    )
      body.steam_id = target.trim();
    if (form.team && form.action !== "invite" && form.action !== "sync")
      body.expected_revision = form.team.revision;
    if (form.action === "edit" || form.action === "create")
      body = {
        ...body,
        name: name.trim(),
        tag: tag.trim(),
        country_code: country.trim(),
        url: website.trim(),
      };
    onSubmit(form.action, form.team?.team_id, body);
  }
  return (
    <Dialog
      open
      onClose={locked ? () => {} : onClose}
      title={actionTitles[form.action]}
      description={
        form.team
          ? `${form.team.snapshot.name} · Dota team ${form.team.team_id}`
          : form.action === "create"
            ? "Create a Dota team managed by a dedicated bot."
            : "Use a team that already exists in Dota."
      }
      size="sm"
    >
      <form onSubmit={submit} className="space-y-5">
        <fieldset disabled={locked} className="space-y-4 disabled:opacity-60">
          {["logo", "banner", "sponsor", "base"].includes(form.action) && (
            <div className="space-y-3">
              <label className="block space-y-2 text-sm">
                {actionTitles[form.action]} image
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  disabled={readingImage}
                  onChange={(event) =>
                    void selectImage(event.target.files?.[0])
                  }
                  className="block w-full text-sm"
                />
              </label>
              <p className="text-xs text-ink-dim">
                PNG or JPEG, up to 512 KiB and 4 megapixels. Uploaded to Steam
                and applied to the Dota team after confirmation.
              </p>
              {image && (
                <img
                  src={image}
                  alt="Selected team artwork preview"
                  className="max-h-48 max-w-full rounded-lg object-contain"
                />
              )}
              {imageError && (
                <p role="alert" className="text-sm text-bad">
                  {imageError}
                </p>
              )}
            </div>
          )}
          {(form.action === "description" || form.action === "create") && (
            <label className="block space-y-2 text-sm">
              Description
              <textarea
                className="w-full rounded-lg border border-line bg-surface-1 px-3 py-2"
                rows={4}
                maxLength={4000}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <span className="block text-xs text-ink-dim">
                Shown on this team page. Dota does not have a team description
                field.
              </span>
            </label>
          )}
          {(form.action === "import" || form.action === "create") && (
            <>
              {form.action === "import" && (
                <>
                  <p className="text-sm text-ink-dim">
                    The dedicated bot must already be a member and administrator
                    of this team.
                  </p>
                  <label className="block space-y-2 text-sm">
                    Dota team ID
                    <TextInput
                      value={teamId}
                      onChange={(e) => setTeamId(e.target.value)}
                      inputMode="numeric"
                      placeholder="123456"
                      required
                    />
                  </label>
                </>
              )}
              <label className="block space-y-2 text-sm">
                Managing bot
                <Select
                  value={botId}
                  onChange={(e) => setBotId(e.target.value)}
                  disabled={
                    bots.isPending || bots.isError || !bots.data?.length
                  }
                  required
                >
                  <option value="">
                    {bots.isPending
                      ? "Loading team bots…"
                      : "Select a team bot"}
                  </option>
                  {bots.data?.map((bot) => (
                    <option key={bot.bot_id} value={bot.bot_id}>
                      {bot.profile_name || bot.username} · {bot.status}
                    </option>
                  ))}
                </Select>
              </label>
              {bots.isError && (
                <div role="alert" className="space-y-2 text-sm text-bad">
                  <p>Could not load team bots. {failureMessage(bots.error)}</p>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void bots.refetch()}
                  >
                    Retry bot list
                  </Button>
                </div>
              )}
              {bots.isSuccess && bots.data.length === 0 && (
                <p className="text-sm text-ink-dim">No bots are configured.</p>
              )}
              {!!selectedBot && capacity.isFetching && (
                <p role="status" className="text-sm text-ink-dim">
                  Checking this bot’s capacity…
                </p>
              )}
              {!!selectedBot &&
                !capacity.isFetching &&
                !capacity.isError &&
                capacity.data &&
                ((capacity.data.policy_max_managed_teams != null &&
                  capacity.data.managed_teams >=
                    capacity.data.policy_max_managed_teams) ||
                  capacity.data.reserved_new_teams > 0 ||
                  (form.action === "create" && !capacity.data.can_create)) && (
                  <p className="text-sm text-ink-dim">
                    {capacity.data.policy_max_managed_teams != null &&
                    capacity.data.managed_teams >=
                      capacity.data.policy_max_managed_teams
                      ? "This bot has reached its team limit. Choose another bot."
                      : capacity.data.reserved_new_teams > 0
                        ? "An earlier onboarding request is unresolved. Reconnecting the same team can recheck it after bot recovery."
                        : form.action === "create" && !capacity.data.can_create
                          ? "This bot cannot create a team right now. Resolve its pending operations or choose another bot."
                          : null}
                  </p>
                )}
              {!!selectedBot && capacity.isError && (
                <p role="alert" className="text-sm text-bad">
                  {failureMessage(capacity.error)}
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void capacity.refetch()}
                  >
                    Retry capacity check
                  </Button>
                </p>
              )}
            </>
          )}
          {form.action === "invite" && (
            <>
              <label className="block space-y-2 text-sm">
                Player SteamID64
                <TextInput
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  inputMode="numeric"
                  placeholder="76561198000000000"
                  required
                />
              </label>
              <p className="text-sm text-ink-dim">
                The player accepts in their Dota client. Sending an invitation
                does not add them to the roster.
              </p>
            </>
          )}
          {(form.action === "edit" || form.action === "create") && (
            <>
              <label className="block space-y-2 text-sm">
                Team name
                <TextInput
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={256}
                  required
                />
              </label>
              <label className="block space-y-2 text-sm">
                Tag
                <TextInput
                  value={tag}
                  onChange={(e) => setTag(e.target.value)}
                  maxLength={64}
                  required
                />
              </label>
              <label className="block space-y-2 text-sm">
                Country code
                <TextInput
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  maxLength={8}
                />
              </label>
              <label className="block space-y-2 text-sm">
                Website
                <TextInput
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  type="url"
                  maxLength={2048}
                />
              </label>
              {form.action === "edit" && (
                <p className="text-xs text-ink-dim">
                  Empty optional fields keep their current value. Clearing
                  metadata is not supported yet.
                </p>
              )}
            </>
          )}
          {form.action === "kick" && (
            <p className="text-sm leading-relaxed">
              Remove <strong className="break-all font-mono">{target}</strong>{" "}
              from the persistent Dota roster? They would need a new invitation
              to rejoin.
            </p>
          )}
          {form.action === "transfer" && (
            <p className="text-sm leading-relaxed">
              Make <strong className="break-all font-mono">{target}</strong> the
              team administrator? The bot will lose its ability to invite,
              remove, and edit until administration is returned to it.
            </p>
          )}
          {form.action === "leave" && (
            <p className="text-sm leading-relaxed">
              The bot will leave this Dota team. A different current member must
              already be its administrator. This can end the bot’s access to
              team information.
            </p>
          )}
          {form.action === "archive" && (
            <p className="text-sm leading-relaxed">
              Stop managing this team here? The bot stays in the Dota team, the
              roster is retained, and its membership still consumes capacity.
            </p>
          )}
        </fieldset>
        {!!error && (
          <p role="alert" className="text-sm text-bad">
            {failureMessage(error)}
          </p>
        )}
        {locked && (
          <p className="text-sm text-warn">
            This request is being sent or needs its outcome checked. Use the
            request status on the page before starting another action.
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2 border-t border-line pt-4">
          <Button type="button" onClick={onClose} disabled={locked}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant={destructive ? "danger" : "primary"}
            disabled={!valid || locked}
          >
            {actionTitles[form.action]}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
